/**
 * Utility functions for date parsing and formatting
 * Handles multiple date formats that might be stored in Firebase
 */

/**
 * Parse a date string or object into a JavaScript Date object
 * Handles multiple formats:
 * - "DD-MM-YYYY" (e.g., "26-08-2025")
 * - "DD-MMM-YYYY" (e.g., "26-Aug-2025")
 * - "YYYY-MM-DD" (e.g., "2025-08-26")
 * - Firestore timestamps
 * - JavaScript Date objects
 * - Unix timestamps (milliseconds)
 */
export const parseDate = (dateInput) => {
  if (!dateInput) return null;

  // Handle Firestore timestamp
  if (dateInput.seconds) {
    return new Date(dateInput.seconds * 1000);
  }

  // Handle number (Unix timestamp in milliseconds)
  if (typeof dateInput === 'number') {
    return new Date(dateInput);
  }

  // Handle Date object
  if (dateInput instanceof Date) {
    return dateInput;
  }

  // Handle string formats
  if (typeof dateInput === 'string') {
    const trimmed = dateInput.trim();
    
    // Pattern: DD-MM-YYYY (e.g., "26-08-2025")
    const ddMmYyyyPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
    const ddMmYyyyMatch = trimmed.match(ddMmYyyyPattern);
    if (ddMmYyyyMatch) {
      const [, day, month, year] = ddMmYyyyMatch;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    // Pattern: DD-MMM-YYYY (e.g., "26-Aug-2025")
    const ddMmmYyyyPattern = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/;
    const ddMmmYyyyMatch = trimmed.match(ddMmmYyyyPattern);
    if (ddMmmYyyyMatch) {
      const [, day, month, year] = ddMmmYyyyMatch;
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      const monthIndex = monthNames.findIndex(m => 
        m.toLowerCase() === month.toLowerCase()
      );
      if (monthIndex !== -1) {
        return new Date(parseInt(year), monthIndex, parseInt(day));
      }
    }

    // Pattern: YYYY-MM-DD (e.g., "2025-08-26")
    const yyyyMmDdPattern = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
    const yyyyMmDdMatch = trimmed.match(yyyyMmDdPattern);
    if (yyyyMmDdMatch) {
      const [, year, month, day] = yyyyMmDdMatch;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    // Try default Date constructor as fallback
    const fallbackDate = new Date(trimmed);
    if (!isNaN(fallbackDate.getTime())) {
      return fallbackDate;
    }
  }

  return null;
};

/**
 * Calculate if a policy is expired based on its expiry date
 * @param {any} expiryDate - The expiry date in any supported format
 * @returns {string} - "Active", "Expired", or the original status if provided
 */
export const calculatePolicyStatus = (expiryDate, originalStatus = null) => {
  if (!expiryDate) return originalStatus || "Active";

  // Handle special statuses
  if (originalStatus === "Pending") return "Pending";
  if (originalStatus === "Renewed") return "Renewed";
  if (originalStatus === "New Lead") return "New Lead";

  const parsedDate = parseDate(expiryDate);
  if (!parsedDate || isNaN(parsedDate.getTime())) {
    return originalStatus || "Active";
  }

  // Normalize dates to start of day for accurate comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const expiry = new Date(parsedDate);
  expiry.setHours(0, 0, 0, 0);

  // If current date is after expiry date, it's expired
  const isExpired = today > expiry;
  return isExpired ? "Expired" : "Active";
};

/**
 * Format a date to DD-MM-YYYY string format
 * @param {any} dateInput - The date to format
 * @returns {string} - Formatted date string or empty string if invalid
 */
export const formatDateToDDMMYYYY = (dateInput) => {
  const parsedDate = parseDate(dateInput);
  if (!parsedDate || isNaN(parsedDate.getTime())) {
    return "";
  }

  const day = String(parsedDate.getDate()).padStart(2, '0');
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const year = parsedDate.getFullYear();

  return `${day}-${month}-${year}`;
};
